/**
 * Cart drawer colourway upsell.
 * Defines <ys-cart-upsell>, rendered by snippets/ys-cart-upsell.liquid at the top
 * of the cart drawer. Colourways are visible immediately; picking one opens the
 * size panel. A single persistent "Adaugă în coș" button (styled like the ATC/
 * checkout button) either guides focus to the next required step or performs the
 * add once a colour+size are chosen. Reuses the same routes/fetchConfig()/
 * cart-drawer.renderContents() plumbing as ys-bundle-builder.js.
 */
(function () {
  if (customElements.get('ys-cart-upsell')) return;

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (char) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }

  class YsCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const payloadScript = this.querySelector('script[data-ys-cu-payload]');
      if (!payloadScript) return;

      try {
        this.payload = JSON.parse(payloadScript.textContent);
      } catch (e) {
        console.error('ys-cart-upsell: invalid payload', e);
        return;
      }

      this.state = {
        openProductId: null,
        selectedVariantId: null,
        submitting: false,
        errorMessage: null,
      };

      this.zones = {
        colorways: this.querySelector('[data-ys-cu-colorways]'),
        sizePanel: this.querySelector('[data-ys-cu-size-panel]'),
        confirm: this.querySelector('[data-ys-cu-confirm]'),
        error: this.querySelector('[data-ys-cu-error]'),
      };

      this._handleClick = this.handleClick.bind(this);
      this.addEventListener('click', this._handleClick);

      this.renderSizePanel();
      this.renderConfirmButton();
      this.renderError();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._handleClick);
    }

    handleClick(event) {
      const target = event.target.closest('[data-action]');
      if (!target || !this.contains(target)) return;
      const action = target.dataset.action;

      if (action === 'toggle-panel') {
        if (this.state.submitting) return;
        const productId = target.dataset.productId;
        this.state.openProductId = this.state.openProductId === productId ? null : productId;
        this.state.selectedVariantId = null;
        this.renderColorwayStates();
        this.renderSizePanel();
        this.renderConfirmButton();
        if (this.state.openProductId === productId) {
          const firstBtn = this.zones.sizePanel.querySelector('.ys-cu__size-btn');
          if (firstBtn) firstBtn.focus();
        }
      } else if (action === 'close-panel') {
        const productId = this.state.openProductId;
        this.state.openProductId = null;
        this.state.selectedVariantId = null;
        this.renderColorwayStates();
        this.renderSizePanel();
        this.renderConfirmButton();
        const triggerBtn = this.zones.colorways.querySelector(`[data-ys-cu-colorway-btn="${productId}"]`);
        if (triggerBtn) triggerBtn.focus();
      } else if (action === 'select-size') {
        if (this.state.submitting) return;
        this.state.selectedVariantId = target.dataset.variantId;
        this.renderSizePanel();
        this.renderConfirmButton();
      } else if (action === 'confirm-add') {
        this.onConfirmOrGuide();
      }
    }

    renderColorwayStates() {
      this.zones.colorways.querySelectorAll('.ys-cu__colorway').forEach((btn) => {
        const isOpen = this.state.openProductId === btn.dataset.productId;
        btn.setAttribute('aria-expanded', String(isOpen));
      });
    }

    renderSizePanel() {
      const productId = this.state.openProductId;
      if (!productId) {
        this.zones.sizePanel.innerHTML = '';
        this.zones.sizePanel.hidden = true;
        return;
      }

      const product = this.payload.products.find((p) => String(p.productId) === productId);
      if (!product) {
        this.zones.sizePanel.innerHTML = '';
        this.zones.sizePanel.hidden = true;
        return;
      }

      const availableVariants = product.variants.filter((v) => v.available);
      const disabled = this.state.submitting;

      const sizeButtons = availableVariants
        .map((v) => {
          const selected = this.state.selectedVariantId === String(v.id);
          return `
            <button type="button" class="ys-cu__size-btn" data-action="select-size" data-variant-id="${v.id}" aria-pressed="${selected}" aria-disabled="${disabled}">
              ${escapeHtml(v.size)}
            </button>
          `;
        })
        .join('');

      const title = this.state.submitting
        ? this.payload.copy.addingText
        : `${this.payload.copy.sizePanelPrefix} · ${product.label}`;

      this.zones.sizePanel.hidden = false;
      this.zones.sizePanel.innerHTML = `
        <div class="ys-cu__size-panel-header">
          <p class="ys-cu__size-panel-title">${escapeHtml(title)}</p>
          <button type="button" class="ys-cu__size-panel-close" data-action="close-panel" aria-disabled="${disabled}" aria-label="${escapeHtml(this.payload.copy.closeLabel)}">&times;</button>
        </div>
        <div class="ys-cu__sizes">
          ${availableVariants.length ? sizeButtons : `<p class="ys-cu__no-sizes">${escapeHtml(this.payload.copy.noSizes)}</p>`}
        </div>
      `;
    }

    renderConfirmButton() {
      const label = this.state.submitting ? this.payload.copy.addingText : this.payload.copy.buttonLabel;
      const ready = this.state.selectedVariantId != null && !this.state.submitting;

      this.zones.confirm.innerHTML = `
        <button type="button" class="button button--primary ys-cu__confirm-btn" data-action="confirm-add" aria-disabled="${!ready}">
          ${escapeHtml(label)}
        </button>
      `;
    }

    renderError() {
      this.zones.error.hidden = !this.state.errorMessage;
      this.zones.error.textContent = this.state.errorMessage || '';
    }

    onConfirmOrGuide() {
      if (this.state.submitting) return;

      // Ready to submit: colour + size already chosen.
      if (this.state.selectedVariantId != null) {
        const product = this.payload.products.find(
          (p) => String(p.productId) === this.state.openProductId
        );
        const variant =
          product && product.variants.find((v) => String(v.id) === this.state.selectedVariantId);
        if (product && variant) this.onSubmit(variant);
        return;
      }

      // Not ready: guide the customer to whichever step is still missing.
      if (this.state.openProductId) {
        const firstBtn = this.zones.sizePanel.querySelector('.ys-cu__size-btn');
        if (firstBtn) firstBtn.focus();
      } else {
        const firstColorway = this.zones.colorways.querySelector('.ys-cu__colorway');
        if (firstColorway) firstColorway.focus();
      }
    }

    onSubmit(variant) {
      if (this.state.submitting || this.state.selectedVariantId == null) return;

      this.state.submitting = true;
      this.state.errorMessage = null;
      this.renderSizePanel();
      this.renderConfirmButton();
      this.renderError();

      const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items: [{ id: variant.id, quantity: 1 }] };

      if (cartEl) {
        body.sections = cartEl.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
        if (typeof cartEl.setActiveElement === 'function') {
          cartEl.setActiveElement(document.activeElement);
        }
      }

      const config = fetchConfig('json');
      config.body = JSON.stringify(body);

      fetch(window.routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.state.submitting = false;
            this.state.errorMessage =
              response.description || response.message || this.payload.copy.errorGeneric;
            this.renderSizePanel();
            this.renderConfirmButton();
            this.renderError();
            return;
          }

          if (cartEl) {
            // renderContents() only clears .is-empty from the inner .drawer__inner node;
            // the outer element's own .is-empty class must be cleared here too.
            if (cartEl.classList.contains('is-empty')) cartEl.classList.remove('is-empty');
            cartEl.renderContents(response);
          } else {
            window.location = window.routes.cart_url;
          }
          // #CartDrawer's HTML — including this element — gets fully replaced by
          // renderContents() above, so no local state reset is needed here.
        })
        .catch((e) => {
          console.error(e);
          this.state.submitting = false;
          this.state.errorMessage = this.payload.copy.errorGeneric;
          this.renderSizePanel();
          this.renderConfirmButton();
          this.renderError();
        });
    }
  }

  customElements.define('ys-cart-upsell', YsCartUpsell);
})();
